import React, { useState, useEffect } from 'react'
import { Menu, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import OptimizedImage from './OptimizedImage'
import LanguageSwitcher from './LanguageSwitcher'
import { motion } from 'framer-motion'

const Header = () => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  
  const navItems = [
    { name: t('nav.home'), href: '#home' },
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.academics'), href: '#academics' },
    { name: t('nav.gallery'), href: '#gallery' },
    { name: t('nav.alumni'), href: '#alumni' },
    { name: t('nav.admissions'), href: '#admissions' },
    { name: t('nav.contact'), href: '#contact' }
  ]

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg' : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8" aria-label="Primary">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo and School Name */}
          <div className="flex items-center space-x-3">
            <OptimizedImage
              src="/logo.jpeg" 
              alt="Shri V.D. Patel Madhyamik School Logo" 
              className="h-15 w-15 lg:h-12 lg:w-12 rounded-full object-cover"
              loading="eager"
              width={70}
              height={70}
            />
            <div className="flex flex-col">
              <p className="text-xl lg:text-xl md:ml-2 font-bold text-primary-purple font-poppins" aria-label="Shri V.D. Patel Madhyamik School">
                Shri V.D. Patel <br /> Madhyamik School
              </p>
              <p className="text-xs text-gray-600 hidden sm:block">
                {t('description')}
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-gray-700 hover:text-primary-purple font-medium transition-colors duration-300 relative group"
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-purple transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
            <LanguageSwitcher className="mx-4" />
            <a 
              href="#admissions"
              className="bg-primary-purple text-white  px-6 py-2 rounded-lg hover:bg-primary-magenta hover:scale-105 transition-all duration-300 font-medium inline-block"
            >
              {t('nav.admissions')} Open
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-primary-purple transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block px-3 py-2 text-gray-700 hover:text-primary-purple font-medium transition-colors duration-300"
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </a>
              ))}
              <div className="px-3 py-2">
                <LanguageSwitcher />
              </div>
              <a
                href="#admissions" 
                className="w-full mt-4 bg-primary-purple text-white px-6 py-2 rounded-lg hover:bg-primary-magenta transition-all duration-300 font-medium inline-block text-center"
                onClick={() => setIsOpen(false)}
              >
                {t('nav.admissions')} Open
              </a>
            </div>
          </div>
        )}
      </nav>
    </motion.header>
  )
}

export default Header